import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  User as UserIcon,
  Mail,
  Calendar,
  Crown,
  LogOut,
  BarChart3,
  MessageSquare,
  Lock
} from "lucide-react";

const LOGO_URL = "/doslions-logo.png";

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-xl border-b border-amber-500/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="text-zinc-400 hover:text-amber-500"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div 
                className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate("/dashboard")}
                data-testid="logo-home-link"
              >
                <img src={LOGO_URL} alt="ECOLIONS" className="w-10 h-10 object-contain" />
                <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                  <span className="text-amber-500">ECOLIONS</span> AI
                </span>
              </div>
            </div>
            <Button 
              variant="ghost"
              onClick={handleLogout}
              className="text-zinc-400 hover:text-amber-500"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <p className="text-xs tracking-[0.3em] text-amber-500 font-bold uppercase mb-2">
            Account
          </p>
          <h1 
            className="text-4xl font-bold tracking-tight uppercase"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            Profile Settings
          </h1>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-[#111111] border border-zinc-800 p-6">
              <h2 
                className="text-xl font-bold tracking-tight uppercase mb-6"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                Account Information
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-zinc-800">
                  <div className="w-12 h-12 bg-amber-500/10 flex items-center justify-center">
                    <UserIcon className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Name</p>
                    <p className="font-semibold">{user?.name || 'User'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-zinc-800">
                  <div className="w-12 h-12 bg-amber-500/10 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Email</p>
                    <p className="font-semibold">{user?.email || 'user@example.com'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-zinc-800">
                  <div className="w-12 h-12 bg-amber-500/10 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Member Since</p>
                    <p className="font-semibold">{user?.created_at ? formatDate(user.created_at) : 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Status */}
            <div className="bg-[#111111] border border-zinc-800 p-6">
              <h2 
                className="text-xl font-bold tracking-tight uppercase mb-6"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                Subscription
              </h2>
              
              <div className={`flex items-center gap-4 p-4 border ${
                user?.is_subscribed 
                  ? 'bg-amber-500/10 border-amber-500/30' 
                  : 'bg-[#0A0A0A] border-zinc-800'
              }`}>
                <div className={`w-12 h-12 flex items-center justify-center ${
                  user?.is_subscribed ? 'bg-amber-500/20' : 'bg-zinc-800'
                }`}>
                  {user?.is_subscribed 
                    ? <Crown className="w-6 h-6 text-amber-500" />
                    : <Lock className="w-6 h-6 text-zinc-500" />
                  }
                </div>
                <div className="flex-1">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Current Plan</p>
                  <p className="font-semibold">{user?.is_subscribed ? 'Pro Ecopreneur' : 'Free Plan'}</p>
                  {user?.is_subscribed ? (
                    <p className="text-xs text-amber-500 mt-1">LIONAI Unlocked</p>
                  ) : (
                    <p className="text-xs text-zinc-500 mt-1">LIONAI Locked</p>
                  )}
                </div>
                {!user?.is_subscribed && (
                  <Button 
                    onClick={() => navigate("/subscription")}
                    className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider"
                    data-testid="upgrade-btn"
                  >
                    Upgrade
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <div className="bg-[#111111] border border-zinc-800 p-6">
              <h2 
                className="text-lg font-bold tracking-tight uppercase mb-4"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                Quick Links
              </h2>
              <div className="space-y-2">
                <Button 
                  variant="ghost"
                  onClick={() => navigate("/dashboard")}
                  className="w-full justify-start text-zinc-400 hover:text-amber-500 hover:bg-zinc-800"
                  data-testid="quick-dashboard-btn"
                >
                  <BarChart3 className="w-4 h-4 mr-3" />
                  Markets Dashboard
                </Button>
                <Button 
                  variant="ghost"
                  onClick={() => navigate("/chat")}
                  className="w-full justify-start text-zinc-400 hover:text-amber-500 hover:bg-zinc-800"
                  data-testid="quick-chat-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-3" />
                  LIONAI Chat
                  {!user?.is_subscribed && <Lock className="w-3 h-3 ml-auto text-zinc-600" />}
                </Button>
                <Button 
                  variant="ghost"
                  onClick={() => navigate("/subscription")}
                  className="w-full justify-start text-zinc-400 hover:text-amber-500 hover:bg-zinc-800"
                  data-testid="quick-subscription-btn"
                >
                  <Crown className="w-4 h-4 mr-3" />
                  Subscription
                </Button>
              </div>
            </div>

            {/* Brand */}
            <div className="bg-[#111111] border border-amber-500/20 p-6">
              <p className="text-xs tracking-[0.2em] text-amber-500 font-bold uppercase mb-2">
                Powered by
              </p>
              <p className="font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                doslions.com
              </p>
              <p className="text-xs text-zinc-500 mt-2">
                Ecopreneur Mindset
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-black/90 backdrop-blur-xl border-t border-amber-500/20">
        <div className="flex items-center justify-around py-3">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate("/dashboard")}
            className="flex-col h-auto py-2 text-zinc-500"
            data-testid="mobile-nav-markets"
          >
            <BarChart3 className="w-5 h-5 mb-1" />
            <span className="text-xs">Markets</span>
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate("/chat")}
            className="flex-col h-auto py-2 text-zinc-500"
            data-testid="mobile-nav-chat"
          >
            <MessageSquare className="w-5 h-5 mb-1" />
            <span className="text-xs">AI Chat</span>
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate("/profile")}
            className="flex-col h-auto py-2 text-amber-500"
            data-testid="mobile-nav-profile"
          >
            <UserIcon className="w-5 h-5 mb-1" />
            <span className="text-xs">Profile</span>
          </Button>
        </div>
      </nav>
    </div>
  );
}
